<template>
  <div class="mt-16 mb-12">
    <h3 class="matched-title mb-3">
      Matched Properties ({{ matchedProperties.length || 0 }})
    </h3>
    <div class="d-flex justify-space-between">
      <v-text-field
        class="matched-filter"
        solo
        flat
        dense
        color="#929292"
        label="Filter by Name"
        @input="val => $emit('search-matched', val)"
      >
        <template #prepend-inner>
          <v-icon
            size="16"
            color="#929292"
          >
            $faSearch
          </v-icon>
        </template>
      </v-text-field>
      <div
        id="sort-selector"
        class="d-flex align-end"
      >
        <span class="sort-text">SORT BY:</span>
        <v-select
          v-model="sortSelected"
          :style="sortFieldWidth"
          :value="sortSelected"
          hide-details
          dense
          flat
          height="20px"
          :items="items"
          append-icon="$faChevronDown"
          :menu-props="{offsetY: true}"
          @input="handleMatchedSort"
        >
          <template #selection="{item}">
            <span
              class="sort-text"
              style="padding:3px 0 0px 3px;"
            >{{ item.text.toUpperCase() }}</span>
          </template>
        </v-select>
      </div>
    </div>
    <div
      v-if="state === status.LOADING"
      class="mt-8 px-2"
    >
      <v-row>
        <div
          class="d-flex justify-space-between"
          style="width:100%;"
        >
          <v-skeleton-loader
            class="address-loader"
            type="button"
          />
          <v-skeleton-loader
            type="button"
            class="button-loader"
          />
          <v-skeleton-loader
            type="button"
            class="address-loader"
          />
        </div>
      </v-row>
      <v-row>
        <div
          class="d-flex justify-space-between mt-4"
          style="width:100%;"
        >
          <v-skeleton-loader
            class="address-loader"
            type="button"
          />
          <v-skeleton-loader
            type="button"
            class="button-loader"
          />
          <v-skeleton-loader
            type="button"
            class="address-loader"
          />
        </div>
      </v-row>
    </div>
    <span
      v-else-if="matchedProperties.length === 0 && !newestMatch"
      class="no-prop-message"
    >
      No properties have been matched
    </span>
    <div
      v-else
      class="mt-2"
    >
      <div class="filter-container" />
      <div
        v-if="newestMatch"
        id="new-match-container"
        class="mb-7"
      >
        <matched-property-item
          :newest-match="newestMatch"
          @handleUnmatch="handleUnmatch"
        />
      </div>
      <div
        class="mt-2"
      >
        <div class="filter-container" />
        <div
          v-for="matched of matchedProperties"
          :key="matched.id"
          class="mb-7"
        >
          <div class="d-flex align-center justify-center">
            <v-card
              class="my-auto"
              color="#F1EDE6"
              width="100%"
              min-width="30%"
              height="auto"
            >
              <v-card-title>
                <div class="matched-header d-flex justify-space-between">
                  <span class="prop-name">{{ matched.noiseaware.name }}</span>
                  <span class="prop-id">ID: {{ matched.noiseaware.id }}</span>
                </div>
              </v-card-title>
              <v-card-text class="matched-address">
                {{ matched.noiseaware.address }}
              </v-card-text>
            </v-card>
            <div class="d-flex justify-center align-center">
              <div class="separator-line" />
              <v-btn
                color="#CECECE"
                class="unmatch-btn"
                @click="() => handleUnmatch(matched)"
              >
                UNMATCH
              </v-btn>
              <div class="separator-line" />
            </div>
            <v-card
              class="my-auto"
              color="#F1EDE6"
              width="100%"
              min-width="30%"
              height="auto"
            >
              <v-card-title>
                <div class="matched-header d-flex justify-space-between">
                  <span class="prop-name">{{ matched.partner.name }}</span>
                  <span class="prop-id">ID: {{ matched.partner.id }}</span>
                </div>
              </v-card-title>
              <v-card-text class="matched-address">
                {{ matched.partner.address }}
              </v-card-text>
            </v-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import status from '@/helpers/property-matching-status'
  import MatchedPropertyItem from './MatchedPropertyItem.vue'

  export default {
    name: 'MatchedProperties',
    components: { MatchedPropertyItem },
    props: {
      state: {
        type: String,
        default: status.LOADING,
      },
      matchedProperties: {
        type: Array,
      },
      newestMatch: {
        type: Object,
        default: null,
      },
    },
    data: () => ({
      sortSelected: '',
      sortFieldWidth: 'width: 120px',
      newMatch: {},
    }),
    computed: {
      status () { return status },
      totalMatched () {
        return this.matchedProperties.length
      },
      items () {
        return [
          { text: 'Oldest First', value: { key: 'updated_at', order: 'DESC' } },
          { text: 'Newest First', value: { key: 'updated_at', order: 'ASC' } },
          { text: 'ASC (Noiseaware)', value: { key: 'noiseaware_name', order: 'ASC' } },
          { text: `ASC (${this.$route.params.partnerName})`, value: { key: 'partner_name', order: 'ASC' } },
        ]
      },
    },
    watch: {
      sortSelected: function (newValue, oldValue) {
        const textString = this.items.find(item => item.value === newValue)
        // since the partner name can be of any length really, need to compute the width so that text doesn't wrap
        if (oldValue !== '') { this.sortFieldWidth = `width: ${textString.text.length * 10}px !important;` }
      },
    },
    created () {
      this.sortSelected = { text: 'Newest First', value: { key: 'updated_at', order: 'ASC' } }
    },
    methods: {
      handleUnmatch (matched) {
        this.$emit('unmatch', matched)
      },
      handleMatchedSort (val) {
        this.$emit('update:sortSelected', val)
      },
    },
  }
</script>
<style lang="scss">
.no-prop-message {
  font-family: Degular, sans-serif;
  font-weight: 400;
  font-size: 36px;
  letter-spacing: 1px;
  color: #929292;
}
  .matched-title {
    font-size: 20px !important;
    font-weight: 400 !important;
  }
  .matched-filter {
    max-width: 41% !important;
    width: 41%;
  }
  .matched-header {
    width:100%;
    font-size: 15px;
    line-height:18px;
    .prop-name {
      font-weight: 700;
      width: 80%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .prop-id {
      white-space: nowrap;
    }
  }
  .matched-address {
    width: 80%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .separator-line {
    height: 2px;
    background-color: $success;
    width: auto;
    min-width: 40px;
  }
  .unmatch-btn.v-btn:not(.v-btn--round).v-size--default {
    padding: 0 10px !important;
    min-width: 116px !important;

  }
  .address-loader {
    .v-skeleton-loader__button {
      width: 470px;
      min-width: 100px;
      max-width:480px;
    }
  }
  .button-loader {
     .v-skeleton-loader__button {
      width: 100px;
      min-width: 50px;
      max-width: 100px;
    }
    margin: 0 15px !important;
  }
  .filter-container {
    width:41.5%;
    margin-bottom: 25px;
  }

  .v-select.v-input--dense .v-select__selection--comma {
    margin: 0 10px !important;
  }
  #sort-selector {
    .v-input__slot:before {
      border: none !important;
    }
    .v-input__slot:after {
      border: none !important;
    }
  }
  .sort-text {
      font-family: Lato, sans-serif;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 1.5px;
  }
</style>
